import type { NumericPoint, NumericSeries, PlotRequest } from "@tento-data";
import { PlotCollection } from "@tento-data";

import type { CanvasRenderTarget } from "../canvas/canvas-types";
import type { CanvasSeriesRenderer } from "./renderer";
import { canonicalOffset, type RuleOffsetType } from "./hrule-renderer";
import { smoothThrough } from "./path-smoothing";
import { effectiveYExtent, toCanvasX, toCanvasY } from "./scale-utils";

export type RegressionCurveRendererStyle = Readonly<{
  strokeStyle: string;
  lineWidth: number;
}>;

/** Draws the smoothed derived channel for a scalar series. */
export class RegressionCurveRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: RegressionCurveRendererStyle = {
    strokeStyle: "#666666",
    lineWidth: 2,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isScalarEntry(entry) && entry.derived?.smoothed !== null &&
      entry.derived?.smoothed !== undefined;
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isScalarEntry(entry)) return;
    const points = entry.derived?.smoothed?.range(
      request.paddedRange.start,
      request.paddedRange.end,
    ).points() ?? [];
    drawPolyline(
      target,
      request,
      entry,
      points,
      entry.derived?.segments ?? null,
      this.style.strokeStyle,
      this.style.lineWidth,
    );
  }
}

export type RegressionPointsRendererStyle = Readonly<{
  fillStyle: string;
  radius: number;
}>;

/** Draws smoothed derived-channel points for a scalar series. */
export class RegressionPointsRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: RegressionPointsRendererStyle = {
    fillStyle: "rgb(255, 0, 0)",
    radius: 2,
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isScalarEntry(entry) && entry.derived?.smoothed !== null &&
      entry.derived?.smoothed !== undefined;
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isScalarEntry(entry)) return;
    const extent = effectiveYExtent(entry, request, request.paddedRange);
    if (extent == null) return;
    const points = entry.derived?.smoothed?.range(
      request.paddedRange.start,
      request.paddedRange.end,
    ).points() ?? [];

    // Points never bridge a gap, so they need no segment splitting.
    target.ctx.fillStyle = this.style.fillStyle;
    for (const [x, y] of points) {
      target.ctx.beginPath();
      target.ctx.arc(
        toCanvasX(x, request.viewRange, target.size.width),
        toCanvasY(y, extent, target.size.height),
        this.style.radius,
        0,
        Math.PI * 2,
      );
      target.ctx.fill();
    }
  }
}

export type RegressionBandRendererStyle = Readonly<{
  fillStyle: string;
}>;

/** Draws the confidence ribbon around a smoothed scalar series. */
export class RegressionBandRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: RegressionBandRendererStyle = {
    fillStyle: "rgba(245, 158, 11, 0.1)",
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return hasSmoothedConfidence(entry);
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isScalarEntry(entry)) return;
    const bands = confidenceBandPoints(entry, request);
    if (bands.length === 0) return;
    const extent = effectiveYExtent(entry, request);
    if (extent == null) return;

    const ranges = segmentRanges(entry.derived?.segments ?? null);
    target.ctx.fillStyle = this.style.fillStyle;
    for (const group of splitByRanges(bands, (b) => b.x, ranges)) {
      drawRibbon(target, request, extent, group);
    }
  }
}

export type RegressionFillRendererStyle = Readonly<{
  offset: number;
  offsetType: RuleOffsetType;
  center: Option<number>;
  topFillStyle: string;
  bottomFillStyle: string;
}>;

/**
 * Exceedance fill (matches the legacy `RegressionFill` →
 * `fillAreaToHorizontalRule`): red caps the area between the top rule
 * (`center + offset`) and the smoothed curve ONLY where the curve rises ABOVE
 * the top rule; blue caps the area between the bottom rule (`center − offset`)
 * and the curve ONLY where the curve drops BELOW the bottom rule. Where the
 * curve stays within the rules (the common case) nothing fills — only its
 * excursions past a rule are highlighted.
 */
export class RegressionFillRenderer implements CanvasSeriesRenderer {
  constructor(private readonly style: RegressionFillRendererStyle = {
    offset: 0.1,
    offsetType: "pct",
    center: null,
    topFillStyle: "rgba(239, 68, 68, 0.3)",
    bottomFillStyle: "rgba(59, 130, 246, 0.3)",
  }) {}

  supports(entry: PlotCollection.AnyEntry): boolean {
    return PlotCollection.isScalarEntry(entry) && entry.derived?.smoothed !== null &&
      entry.derived?.smoothed !== undefined;
  }

  render(
    target: CanvasRenderTarget,
    request: PlotRequest,
    entry: PlotCollection.AnyEntry,
  ): void {
    if (!PlotCollection.isScalarEntry(entry)) return;
    const extent = effectiveYExtent(entry, request, request.paddedRange);
    if (extent == null) return;
    const points = entry.derived?.smoothed?.range(
      request.paddedRange.start,
      request.paddedRange.end,
    ).points() ?? [];
    if (points.length === 0) return;

    const center = this.style.center ?? entry.descriptor.yRange?.center ??
      (extent.min + extent.max) / 2;
    const offset = canonicalOffset(
      this.style.offset,
      this.style.offsetType,
      extent.min,
      extent.max,
    );
    const ranges = segmentRanges(entry.derived?.segments ?? null);
    for (const group of splitByRanges(points, (p) => p[0], ranges)) {
      if (group.length === 0) continue;
      // Red where the curve EXCEEDS the top rule (fill between the rule and the
      // curve, above the rule); blue where the curve drops BELOW the bottom
      // rule. The `side` is the half-plane the cap lives in: red above its rule,
      // blue below its rule.
      fillToRule(
        target,
        request,
        extent,
        group,
        center + offset,
        this.style.topFillStyle,
        "above",
      );
      fillToRule(
        target,
        request,
        extent,
        group,
        center - offset,
        this.style.bottomFillStyle,
        "below",
      );
    }
  }
}

function hasSmoothedConfidence(entry: PlotCollection.AnyEntry): boolean {
  return PlotCollection.isScalarEntry(entry) &&
    entry.derived?.smoothed !== null &&
    entry.derived?.smoothed !== undefined &&
    entry.derived?.confidence !== null &&
    entry.derived?.confidence !== undefined;
}

/**
 * The [minX, maxX] span of each regression segment, or null when the curve
 * is a single continuous run (nothing to break). Used to split a flat point
 * stream back into its segments so a curve/band/fill never bridges a gap.
 */
function segmentRanges(
  segments: Option<readonly NumericSeries<number>[]>,
): Option<readonly [number, number][]> {
  if (segments == null || segments.length <= 1) return null;
  const ranges: [number, number][] = [];
  for (const seg of segments) {
    const pts = seg.points();
    if (pts.length === 0) continue;
    ranges.push([pts[0]![0], pts[pts.length - 1]![0]]);
  }
  return ranges.length > 1 ? ranges : null;
}

/**
 * Group `items` (ordered by x) into the segments described by `ranges`.
 * Returns a single group when there are no segment breaks.
 */
function splitByRanges<T>(
  items: readonly T[],
  getX: (item: T) => number,
  ranges: Option<readonly [number, number][]>,
): T[][] {
  if (ranges == null) return items.length > 0 ? [items.slice()] : [];
  const groups: T[][] = [];
  let idx = 0;
  let current: T[] = [];
  for (const item of items) {
    while (idx < ranges.length - 1 && getX(item) > ranges[idx]![1]) {
      if (current.length > 0) {
        groups.push(current);
        current = [];
      }
      idx++;
    }
    current.push(item);
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

function drawPolyline(
  target: CanvasRenderTarget,
  request: PlotRequest,
  entry: PlotCollection.AnyEntry,
  points: readonly NumericPoint<number>[],
  segments: Option<readonly NumericSeries<number>[]>,
  strokeStyle: string,
  lineWidth: number,
): void {
  const extent = effectiveYExtent(entry, request, request.paddedRange);
  if (points.length === 0 || extent == null) return;

  const ranges = segmentRanges(segments);
  target.ctx.strokeStyle = strokeStyle;
  target.ctx.lineWidth = lineWidth;
  for (const group of splitByRanges(points, (p) => p[0], ranges)) {
    if (group.length === 0) continue;
    // Smooth (Catmull-Rom spline), matching the legacy LineChartBody curve —
    // a raw polyline of the smoothed points reads as faceted at this scale.
    const canvasPts = group.map(([x, y]) =>
      [
        toCanvasX(x, request.viewRange, target.size.width),
        toCanvasY(y, extent, target.size.height),
      ] as const
    );
    target.ctx.beginPath();
    smoothThrough(target.ctx, canvasPts);
    target.ctx.stroke();
  }
}

function confidenceBandPoints(
  entry: PlotCollection.ScalarEntry,
  request: PlotRequest,
): readonly { readonly x: number; readonly low: number; readonly high: number }[] {
  const smoothed = entry.derived?.smoothed?.range(
    request.paddedRange.start,
    request.paddedRange.end,
  ).points() ?? [];
  const confidence = entry.derived?.confidence?.range(
    request.paddedRange.start,
    request.paddedRange.end,
  ).points() ?? [];
  if (smoothed.length === 0 || confidence.length === 0) return [];

  const confidenceByX = new Map<number, number>();
  for (const [x, y] of confidence) confidenceByX.set(x, y);

  const result: { readonly x: number; readonly low: number; readonly high: number }[] = [];
  for (const [x, y] of smoothed) {
    const delta = confidenceByX.get(x);
    if (delta === undefined) continue;
    result.push({ x, low: y - delta, high: y + delta });
  }
  return result;
}

function drawRibbon(
  target: CanvasRenderTarget,
  request: PlotRequest,
  extent: { readonly min: number; readonly max: number },
  bands: readonly { readonly x: number; readonly low: number; readonly high: number }[],
): void {
  if (bands.length === 0) return;

  const w = target.size.width;
  const h = target.size.height;
  // Smooth BOTH ribbon edges with the same spline as the curve so the band
  // hugs it instead of faceting/ballooning at the edges: the high edge left→
  // right, then the low edge right→left, closed into one filled polygon.
  const highPts = bands.map((b) =>
    [toCanvasX(b.x, request.viewRange, w), toCanvasY(b.high, extent, h)] as const
  );
  const lowPtsRev = bands
    .slice()
    .reverse()
    .map((b) =>
      [toCanvasX(b.x, request.viewRange, w), toCanvasY(b.low, extent, h)] as const
    );

  target.ctx.beginPath();
  smoothThrough(target.ctx, highPts);
  smoothThrough(target.ctx, lowPtsRev, true);
  target.ctx.fill();
}

/**
 * Fill the area between the smoothed curve and a horizontal rule, on one `side`
 * of the rule: `"above"` fills only where the curve rises above the rule,
 * `"below"` only where it drops below.
 *
 * The near edge is the SAME Catmull-Rom spline as the stroked curve (built by
 * `smoothThrough` over the identical canvas points), so the fill top is glued to
 * the drawn curve — no white sliver/gap between them. The far edge runs off the
 * canvas (bottom for `"above"`, top for `"below"`) so it never bounds the fill;
 * a rectangular `clip` at the rule is the only thing that trims the fill, cutting
 * the spline exactly at the rule so nothing bleeds past the cap. Clamping the
 * points to the rule instead (the earlier approach) either faceted the top away
 * from the splined curve (gap) or overshot at the flat↔curve transition (bleed);
 * clip avoids both because it trims the real spline geometrically.
 */
function fillToRule(
  target: CanvasRenderTarget,
  request: PlotRequest,
  extent: { readonly min: number; readonly max: number },
  points: readonly NumericPoint<number>[],
  ruleY: number,
  fillStyle: string,
  side: "above" | "below",
): void {
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) return;

  const { ctx } = target;
  const w = target.size.width;
  const h = target.size.height;
  const canvasPts = points.map(([x, y]) =>
    [
      toCanvasX(x, request.viewRange, w),
      toCanvasY(y, extent, h),
    ] as const
  );
  const ruleCanvasY = toCanvasY(ruleY, extent, h);
  const firstX = canvasPts[0]![0];
  const lastX = canvasPts[canvasPts.length - 1]![0];
  // Far edge lives off-canvas so only the clip trims the fill.
  const farY = side === "above" ? h : 0;

  ctx.save();
  // Clip to the half-plane on the filled side of the rule: "above" the rule in
  // value is a SMALLER canvas-y (top strip), "below" is larger (bottom strip).
  ctx.beginPath();
  if (side === "above") ctx.rect(0, 0, w, ruleCanvasY);
  else ctx.rect(0, ruleCanvasY, w, h - ruleCanvasY);
  ctx.clip();

  ctx.fillStyle = fillStyle;
  ctx.beginPath();
  smoothThrough(ctx, canvasPts);
  ctx.lineTo(lastX, farY);
  ctx.lineTo(firstX, farY);
  ctx.fill();
  ctx.restore();
}
